import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useNotificationQueryContext } from "../../context/NotificationQueryContext";
import {
  formatRelativeTime,
  formatFullTimestamp,
  shouldShowRelativeTime,
  formatAbsoluteDate,
} from "../../utils/notification/notificationHelpers";
import useAccessibility from "../../hooks/notifications/useAccessibility";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";

/**
 * NotificationItem Component
 *
 * Individual notification display with optional click handling
 * Shows actor avatar, message, and timestamp
 * Can be clickable (for dropdown) or selection-only (for main page)
 */
const NotificationItem = React.memo(
  ({
    notification,
    isSelected = false,
    showCheckbox = false,
    onSelect,
    onClose,
    isLast = false,
  }) => {
    const { selectNotification } = useNotificationQueryContext();
    const navigate = useNavigate();

    // Use accessibility hook
    const { getAccessibleProps } = useAccessibility({
      announceChanges: true,
    });

    // Memoized handle selection
    const handleSelect = useCallback(
      (event) => {
        event.stopPropagation();
        if (onSelect) {
          onSelect(notification._id, !isSelected);
        } else {
          // Use context action if no custom handler provided
          selectNotification(notification._id);
        }
      },
      [onSelect, notification._id, isSelected, selectNotification],
    );

    // Memoized handle notification click (for both dropdown and page)
    const handleClick = useCallback(
      (event) => {
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();

        // Handle thread notification navigation
        if (
          ThreadNotificationHandler.isThreadNotification({
            type: notification.type,
          })
        ) {
          const handler = new ThreadNotificationHandler(navigate);
          const threadId = notification.metadata?.threadId;
          const messageId = notification.metadata?.messageId;
          const replyId = notification.metadata?.replyId;
          const fightId = notification.metadata?.fightId;

          if (threadId) {
            handler.navigateToThread(threadId, messageId, replyId, fightId);
          }
        }

        // Handle fighter cancellation notification navigation
        if (notification.type === "fighter_cancelled") {
          const handler = new ThreadNotificationHandler(navigate);
          handler.navigateToLineup(notification.metadata?.eventId);
        }

        // Immediately blur to prevent focus states
        if (event.currentTarget) {
          event.currentTarget.blur();
        }

        // If onClose is provided (dropdown mode), close the dropdown
        if (onClose) {
          // Use setTimeout to ensure blur happens before close
          setTimeout(() => {
            onClose();
          }, 0);
        }
      },
      [onClose, navigate, notification.type, notification.metadata],
    );

    // Memoized actor information
    const actorInfo = useMemo(() => {
      const actor = notification.actor || {};
      return {
        name: notification.metadata?.actorName || actor.name || "Unknown User",
        username: notification.metadata?.actorUsername || actor.username || "",
        picture: notification.metadata?.actorPicture || actor.picture || "",
        verified:
          notification.metadata?.actorVerified || actor.verified || false,
      };
    }, [notification.actor, notification.metadata]);

    // Memoized timestamp formatting
    const timestampInfo = useMemo(() => {
      const timestamp = shouldShowRelativeTime(notification.createdAt)
        ? formatRelativeTime(notification.createdAt)
        : formatAbsoluteDate(notification.createdAt);

      return {
        display: timestamp,
        full: formatFullTimestamp(notification.createdAt),
      };
    }, [notification.createdAt]);

    // Modern CSS classes
    const containerClasses = useMemo(
      () =>
        `notification-item relative overflow-hidden px-4 py-3 select-none touch-manipulation
    ${
      !notification.read
        ? "notification-unread border-l-2 border-l-deep-red"
        : ""
    }
    ${isSelected ? "notification-selected border-l-2 border-l-blue-500" : ""} 
    `,
      [notification.read, isSelected],
    );

    // Memoized notification message
    const notificationMessage = useMemo(() => {
      if (
        ThreadNotificationHandler.isThreadNotification({
          type: notification.type,
        })
      ) {
        const threadText = ThreadNotificationHandler.getThreadNotificationText({
          type: notification.type,
          data: { metadata: notification.metadata },
        });
        return {
          action: threadText.message.replace(actorInfo.name, "").trim(),
          preview:
            threadText.preview ||
            notification.metadata?.messagePreview ||
            notification.metadata?.replyPreview,
          threadTitle: notification.metadata?.threadTitle,
        };
      }

      // Fighter cancellation notification
      if (notification.type === "fighter_cancelled") {
        const { fighterName, eventName } = notification.metadata || {};
        return {
          action: `${fighterName}'s fight has been cancelled for ${eventName}. They will score 0 points.`,
          preview: null,
          threadTitle: null,
          isFighterCancellation: true,
        };
      }

      // Regular notifications
      return {
        action:
          notification.type === "follow"
            ? "started following you"
            : "unfollowed you",
        preview: null,
        threadTitle: null,
      };
    }, [notification.type, notification.metadata, actorInfo.name]);

    // Memoized accessibility props
    const accessibilityProps = useMemo(
      () =>
        getAccessibleProps("notification-item", {
          message: `${actorInfo.name} ${notificationMessage.action}`,
          read: notification.read,
          timestampId: `timestamp-${notification._id}`,
        }),
      [
        getAccessibleProps,
        actorInfo.name,
        notificationMessage.action,
        notification.read,
        notification._id,
      ],
    );

    return (
      <div className={containerClasses} {...accessibilityProps}>
        <div className="flex items-start space-x-3">
          {/* Selection checkbox - NOT clickable for navigation */}
          {showCheckbox && (
            <div className="flex-shrink-0 pt-1">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleSelect}
                onClick={(e) => e.stopPropagation()} // Prevent navigation when clicking checkbox
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                aria-label={`Select notification from ${actorInfo.name}`}
              />
            </div>
          )}

          {/* Clickable content area */}
          <div
            className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-2 -m-2"
            onClick={handleClick}
            tabIndex={-1}
          >
            <div className="flex items-start space-x-3">
              {/* Actor avatar */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {notificationMessage.isFighterCancellation ? (
                    // Warning icon for fighter cancellations
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-orange-100 ring-2 ring-orange-400 flex items-center justify-center">
                      <span
                        className="text-orange-600 text-xl"
                        role="img"
                        aria-label="Warning"
                      >
                        ⚠️
                      </span>
                    </div>
                  ) : (
                    // Regular user avatar
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 ring-1 ring-gray-200">
                      {actorInfo.picture ? (
                        <img
                          src={actorInfo.picture}
                          alt={`${actorInfo.name}'s avatar`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full flex items-center justify-center text-gray-600 text-sm font-semibold ${
                          actorInfo.picture ? "hidden" : "flex"
                        }`}
                      >
                        {actorInfo.name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                  )}
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-deep-red rounded-full border-2 border-white"></div>
                  )}
                </div>
              </div>

              {/* Notification content */}
              <div className="flex-1 min-w-0">
                <div className="space-y-1">
                  {notificationMessage.isFighterCancellation ? (
                    // Fighter cancellation notification
                    <>
                      <p className="text-sm font-semibold text-orange-600 leading-snug">
                        Fight Cancelled
                      </p>
                      <p
                        className={`text-sm leading-snug ${
                          !notification.read
                            ? "font-medium text-gray-900"
                            : "text-gray-700"
                        }`}
                      >
                        {notificationMessage.action}
                      </p>
                    </>
                  ) : (
                    // Regular notification
                    <p
                      className={`text-sm leading-snug ${
                        !notification.read
                          ? "font-medium text-gray-900"
                          : "text-gray-700"
                      }`}
                    >
                      <span className="font-semibold">{actorInfo.name}</span>
                      {actorInfo.verified && (
                        <span className="ml-1 text-gold" title="Verified user">
                          ✓
                        </span>
                      )}
                      <span className="ml-1 font-normal">
                        {notificationMessage.action}
                      </span>
                    </p>
                  )}

                  {/* Thread context for thread notifications */}
                  {notificationMessage.threadTitle && (
                    <p className="text-xs text-gray-600 italic">
                      in "{notificationMessage.threadTitle}"
                    </p>
                  )}

                  {/* Message preview for thread notifications */}
                  {notificationMessage.preview && (
                    <p className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 mt-1">
                      "{notificationMessage.preview}"
                    </p>
                  )}

                  {/* Compact timestamp */}
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    {actorInfo.username && (
                      <>
                        <span>@{actorInfo.username}</span>
                        <span>•</span>
                      </>
                    )}
                    <span
                      id={`timestamp-${notification._id}`}
                      title={timestampInfo.full}
                    >
                      {timestampInfo.display}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

// Display name for debugging
NotificationItem.displayName = "NotificationItem";

export default NotificationItem;
